import { useState } from 'react';
import { Moon, Sun, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import { useThemeStore } from '../../store/themeStore';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../utils/cn';

export default function Navbar() {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { theme, toggleTheme } = useThemeStore();
  const user = useAuthStore((state) => state.user);

  const fleetName = user?.fleet?.name || 'Fleet Dashboard';
  const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'User';
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase() || 'F';

  return (
    <header className="h-16 bg-surface dark:bg-surface-dark border-b border-border dark:border-border-dark flex items-center justify-between px-4 lg:px-6 shadow-sm sticky top-0 z-30 backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95">
      {/* Left Section - Fleet Name */}
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-bold text-text dark:text-text-dark truncate">
          {fleetName}
        </h2>
        <p className="text-xs text-text-secondary dark:text-text-secondary-dark hidden sm:block">
          Fleet Management Portal
        </p>
      </div>

      {/* Right Section - Actions */}
      <div className="flex items-center gap-2 lg:gap-4">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={cn(
            'p-2 rounded-lg transition-all duration-200',
            'hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark',
            'text-text-secondary dark:text-text-secondary-dark hover:text-text dark:hover:text-text-dark',
            'hover:scale-110 active:scale-95'
          )}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </button>

        <NotificationBell />

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-2 lg:gap-3 pl-2 lg:pl-4 pr-2 py-2 rounded-lg hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-all duration-200 group"
          >
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center text-white font-semibold shadow-lg group-hover:scale-110 transition-transform duration-200">
              {userInitials}
            </div>
            <div className="hidden lg:block text-left">
              <p className="text-sm font-semibold text-text dark:text-text-dark">{userName}</p>
              <p className="text-xs text-text-secondary dark:text-text-secondary-dark">Fleet Owner</p>
            </div>
            <ChevronDown className={cn(
              'w-4 h-4 text-text-secondary dark:text-text-secondary-dark hidden lg:block transition-transform duration-200',
              showProfileMenu && 'rotate-180'
            )} />
          </button>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowProfileMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-surface dark:bg-surface-dark rounded-xl shadow-xl border border-border dark:border-border-dark z-20 overflow-hidden">
                <div className="p-4 border-b border-border dark:border-border-dark">
                  <p className="text-sm font-semibold text-text dark:text-text-dark">{userName}</p>
                  <p className="text-xs text-text-secondary dark:text-text-secondary-dark">{user?.email}</p>
                </div>
                <div className="p-2">
                  <Link
                    to="/settings"
                    className="block w-full text-left px-3 py-2 text-sm text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark rounded-lg transition-colors"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    Profile Settings
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
